import { GuestGroup, guestGroups } from "../shared/guest-groups";
import { requireElement } from "./shared/dom";

export function renderGroupTag(value: GuestGroup): string {
  const group = guestGroups[value];
  return `<span class="group-colour-tag" style="--group-colour:${group.colour}"><span class="group-colour-dot" aria-hidden="true"></span>${group.colourName}</span>`;
}

function renderSelection(value: GuestGroup | ""): string {
  return value ? `<span>${guestGroups[value].label}</span>${renderGroupTag(value)}` : '<span class="group-placeholder">Choose your group</span>';
}

export function renderGuestGroupPicker(value: GuestGroup | ""): string {
  return `<div class="guest-group-picker"><label id="guest-group-label" for="guest-group">Your guest group</label>
    <input type="hidden" name="guestGroup" value="${value}" />
    <div class="group-control"><button type="button" id="guest-group" class="group-trigger" aria-haspopup="listbox" aria-expanded="false" aria-controls="guest-group-options" aria-labelledby="guest-group-label guest-group-value" aria-describedby="group-preview">
      <span id="guest-group-value" class="group-selection">${renderSelection(value)}</span><span class="group-chevron" aria-hidden="true"></span>
    </button>
    <div id="guest-group-options" class="group-options" role="listbox" aria-labelledby="guest-group-label" hidden>
      ${Object.values(GuestGroup).map((key) => `<button type="button" role="option" class="group-option" data-value="${key}" aria-selected="${key === value}" tabindex="-1"><span>${guestGroups[key].label}</span>${renderGroupTag(key)}<span class="group-check" aria-hidden="true">✓</span></button>`).join("")}
    </div>
    </div><p class="field-hint" id="group-preview">Choose the group that best fits your invitation. Your colour will appear on your guest card; ushers will confirm your table.</p>
  </div>`;
}

/** A small, dependency-free listbox. Listeners stay on its replaceable form subtree. */
export function bindGuestGroupPicker(container: HTMLElement, onChange: () => void): void {
  const root = container.querySelector<HTMLElement>(".guest-group-picker");
  if (!root) return;
  const trigger = requireElement<HTMLButtonElement>(".group-trigger", root);
  const list = requireElement<HTMLElement>(".group-options", root);
  const input = requireElement<HTMLInputElement>("input[name=guestGroup]", root);
  const options = Array.from(list.querySelectorAll<HTMLButtonElement>("[role=option]"));
  const close = (restoreFocus = false): void => {
    list.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    if (restoreFocus) trigger.focus({ preventScroll: true });
  };
  const open = (last = false): void => {
    list.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    const selected = options.find((option) => option.dataset.value === input.value);
    (selected ?? options[last ? options.length - 1 : 0])?.focus({ preventScroll: true });
  };
  trigger.addEventListener("click", () => list.hidden ? open() : close());
  trigger.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    open(event.key === "ArrowUp");
  });
  options.forEach((option) => option.addEventListener("click", () => {
    const value = option.dataset.value as GuestGroup;
    input.value = value;
    options.forEach((item) => item.setAttribute("aria-selected", String(item === option)));
    requireElement<HTMLElement>(".group-selection", root).innerHTML = renderSelection(value);
    onChange();
    close(true);
  }));
  let search = "";
  let lastKeyTime = 0;
  list.addEventListener("keydown", (event) => {
    const index = options.indexOf(document.activeElement as HTMLButtonElement);
    let next = index;
    if (event.key === "ArrowDown") next = (index + 1) % options.length;
    else if (event.key === "ArrowUp") next = (index - 1 + options.length) % options.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = options.length - 1;
    else if (event.key === "Escape") { event.preventDefault(); close(true); return; }
    else if (event.key === "Tab") {
      // Restore the trigger before normal tab navigation so focus exits the picker.
      close(true);
      return;
    } else if (event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey) {
      search = Date.now() - lastKeyTime > 700 ? event.key : search + event.key;
      lastKeyTime = Date.now();
      next = options.findIndex((option) => guestGroups[option.dataset.value as GuestGroup].label.toLowerCase().startsWith(search.toLowerCase()));
    } else return;
    event.preventDefault();
    options[next]?.focus({ preventScroll: true });
  });
  root.addEventListener("focusout", (event) => {
    if (!root.contains(event.relatedTarget as Node | null)) close();
  });
}
